import React, { useState, useEffect, useCallback, useRef } from "react";
import api, { formatVND, formatApiError, normalizePhone, blockWhitespaceKey } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Sparkles, Receipt, Plus, Layers, X } from "lucide-react";
import DateInputVN from "@/components/DateInputVN";

function timeToMin(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function calcRegularPrice(court, date, startTime, endTime) {
  if (!court || !date || !startTime || !endTime) return { total: 0, perHour: 0 };
  const d = new Date(date + "T00:00:00");
  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
  const dayType = isWeekend ? "weekend" : "weekday";
  const dayPricing = court.pricing?.find((p) => p.day_type === dayType);
  if (!dayPricing) return { total: 0, perHour: 0 };
  const startMin = timeToMin(startTime);
  const endMin = timeToMin(endTime);
  let total = 0;
  let firstPrice = 0;
  for (const slot of dayPricing.time_slots || []) {
    let sStart = timeToMin(slot.start);
    let sEnd = timeToMin(slot.end);
    if (sEnd <= sStart) sEnd += 24 * 60;
    const ovStart = Math.max(startMin, sStart);
    const ovEnd = Math.min(endMin, sEnd);
    if (ovStart < ovEnd) {
      const hours = (ovEnd - ovStart) / 60;
      total += Math.round(hours * slot.price);
      if (!firstPrice) firstPrice = slot.price;
    }
  }
  return { total, perHour: firstPrice };
}

function calcSocialPrice(startTime, guestCount) {
  if (!startTime || !guestCount) return { pricePerPerson: 0, total: 0 };
  const startMin = timeToMin(startTime);
  const pricePerPerson = startMin >= 945 ? 80000 : 50000; // 15:45 chiều = 80k, sáng = 50k
  return { pricePerPerson, total: pricePerPerson * guestCount };
}

const TIME_OPTIONS = [];
for (let h = 5; h < 24; h++) {
  TIME_OPTIONS.push(`${String(h).padStart(2, "0")}:00`);
  TIME_OPTIONS.push(`${String(h).padStart(2, "0")}:30`);
}

export default function BookingModal({ open, onClose, booking, courts, selectedDate, prefillCourtId, prefillTime, onSaved, forceBookingType }) {
  const { user } = useAuth();
  const isStaffOrCeo = user?.role === "ceo" || user?.role === "staff";
  const [bookingType, setBookingType] = useState("regular");
  const [courtId, setCourtId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [date, setDate] = useState(selectedDate);
  const [startTime, setStartTime] = useState("07:00");
  const [endTime, setEndTime] = useState("09:00");
  const [status, setStatus] = useState("pending");
  const [deposit, setDeposit] = useState(0);
  const [notes, setNotes] = useState("");
  const [guestCount, setGuestCount] = useState(4);
  const [organizerName, setOrganizerName] = useState("");
  const [skipCreateCustomer, setSkipCreateCustomer] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Multi-court mode (chỉ CEO/staff, chỉ regular, chỉ tạo mới)
  const [multiSlots, setMultiSlots] = useState([]);  // [{court_id, start_time, end_time}]

  const isEdit = !!booking;
  const socialCourt = courts.find((c) => c.court_type === "social");
  const regularCourts = courts.filter((c) => c.court_type !== "social");
  const selectedCourt = courts.find((c) => c.id === courtId);

  // Price calculation
  const regularPrice = calcRegularPrice(selectedCourt, date, startTime, endTime);
  const socialPrice = calcSocialPrice(startTime, guestCount);
  const durationHours = startTime && endTime ? (timeToMin(endTime) - timeToMin(startTime)) / 60 : 0;

  // Multi-court total: cộng dồn các slot phụ
  const multiSlotsTotal = multiSlots.reduce((sum, s) => {
    const c = courts.find((x) => x.id === s.court_id);
    if (!c || !s.start_time || !s.end_time) return sum;
    const p = calcRegularPrice(c, date, s.start_time, s.end_time);
    return sum + (p.total || 0);
  }, 0);
  const grandTotalRegular = (regularPrice.total || 0) + multiSlotsTotal;

  // Reset form
  const formInitRef = useRef(false);
  useEffect(() => {
    if (!open) { formInitRef.current = false; return; }
    if (formInitRef.current) return;
    formInitRef.current = true;
    setError("");
    if (booking) {
      setBookingType(booking.booking_type || "regular");
      setCourtId(booking.court_id);
      setCustomerName(booking.customer_name);
      setCustomerPhone(booking.customer_phone);
      setCustomerId(booking.customer_id || "");
      setDate(booking.date);
      setStartTime(booking.start_time);
      setEndTime(booking.end_time);
      setStatus(booking.status);
      setDeposit(booking.deposit || 0);
      setNotes(booking.notes || "");
      setGuestCount(booking.social_guest_count || 4);
      setOrganizerName(booking.organizer_name || "");
    } else {
      const sc = courts.find((c) => c.court_type === "social");
      const rc = courts.filter((c) => c.court_type !== "social");
      setBookingType(forceBookingType || (prefillCourtId && sc && prefillCourtId === sc.id ? "social" : "regular"));
      setCourtId(prefillCourtId || (forceBookingType === "social" && sc ? sc.id : rc[0]?.id) || "");
      setCustomerName("");
      setCustomerPhone("");
      setCustomerId("");
      setDate(selectedDate);
      setStartTime(prefillTime || "07:00");
      const prefMinutes = timeToMin(prefillTime || "07:00") + 120;
      const endH = Math.floor(prefMinutes / 60);
      const endM = prefMinutes % 60;
      setEndTime(endH < 24 ? `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}` : "23:30");
      setStatus("pending");
      setDeposit(0);
      setNotes("");
      setGuestCount(4);
      setOrganizerName("");
      setSkipCreateCustomer(false);
      setMultiSlots([]);
    }
  }, [open, booking, courts, forceBookingType, prefillCourtId, prefillTime, selectedDate]);

  // Customer autocomplete
  const searchCustomers = useCallback(async (query) => {
    if (query.length < 1) { setCustomerSuggestions([]); return; }
    try {
      const { data } = await api.get(`/customers/search?q=${encodeURIComponent(query)}`);
      setCustomerSuggestions(data.customers || []);
      setShowSuggestions(true);
    } catch { setCustomerSuggestions([]); }
  }, []);

  const selectCustomer = (c) => {
    setCustomerName(c.name);
    setCustomerPhone(c.phone || "");
    setCustomerId(c.id);
    if (bookingType === "social") setOrganizerName(c.name);
    setShowSuggestions(false);
  };

  useEffect(() => {
    if (bookingType === "social" && socialCourt) {
      setCourtId(socialCourt.id);
    } else if (bookingType === "regular" && selectedCourt?.court_type === "social") {
      setCourtId(regularCourts[0]?.id || "");
    }
  }, [bookingType, socialCourt, selectedCourt, regularCourts]);

  const handleSubmit = async () => {
    setError("");
    setSubmitting(true);
    try {
      // Multi-court mode (CEO/staff, regular, tạo mới, có slots phụ)
      if (!isEdit && bookingType === "regular" && multiSlots.length > 0) {
        // Slots = sân chính + slots phụ
        const allSlots = [
          { court_id: courtId, start_time: startTime, end_time: endTime },
          ...multiSlots.filter((s) => s.court_id && s.start_time && s.end_time),
        ];
        const payload = {
          customer_name: customerName,
          customer_phone: customerPhone,
          customer_id: customerId || null,
          date,
          status,
          deposit: parseInt(deposit) || 0,
          notes,
          skip_create_customer: skipCreateCustomer,
          slots: allSlots,
        };
        onClose();
        const { data } = await api.post("/bookings/multi", payload);
        const { toast } = await import("sonner");
        if (data.created_count > 0) {
          toast.success(data.message || `Đã đặt ${data.created_count} sân`);
        }
        if (data.skipped_count > 0) {
          for (const sk of data.skipped) {
            toast.warning(`${sk.court_name || sk.court_id}: ${sk.reason}`);
          }
        }
        onSaved();
        return;
      }

      const payload = {
        booking_type: bookingType,
        court_id: bookingType === "social" && socialCourt ? socialCourt.id : courtId,
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_id: customerId || null,
        date,
        start_time: startTime,
        end_time: endTime,
        status,
        deposit: parseInt(deposit) || 0,
        notes,
        organizer_name: bookingType === "social" ? (organizerName || customerName) : null,
        social_guest_count: bookingType === "social" ? (parseInt(guestCount) || 0) : 0,
        skip_create_customer: skipCreateCustomer,
      };
      onClose();
      if (isEdit) {
        await api.put(`/bookings/${booking.id}`, payload);
      } else {
        await api.post("/bookings", payload);
      }
      onSaved();
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(err.response?.data?.detail || "Loi khi luu dat san");
      onSaved();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!booking || !window.confirm("Xác nhận xoá đặt sân này?")) return;
    try {
      await api.delete(`/bookings/${booking.id}`);
      onSaved();
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-[420px] max-h-[90vh] overflow-y-auto p-4" data-testid="booking-modal">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-[#F8FAFC] text-base" data-testid="booking-modal-title">
            {isEdit ? "Chỉnh sửa đặt sân" : "Đặt sân"}
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            {isEdit ? "Cập nhật thông tin" : "Tạo đặt sân mới"}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-1.5 text-xs text-red-400" data-testid="booking-error">
            {error}
          </div>
        )}

        <div className="space-y-2.5">
          {/* Booking type toggle */}
          {!forceBookingType && (
          <div className="flex gap-1.5">
            <button onClick={() => setBookingType("regular")}
              className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${bookingType === "regular" ? "bg-lime-500 text-[#0f172a]" : "bg-[#334155] text-slate-400"}`}
              data-testid="type-regular-btn">
              Sân cố định
            </button>
            <button onClick={() => setBookingType("social")}
              className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${bookingType === "social" ? "bg-teal-500 text-[#0f172a]" : "bg-[#334155] text-slate-400"}`}
              data-testid="type-social-btn">
              Social
            </button>
          </div>
          )}

          {/* Customer name with autocomplete */}
          <div className="relative">
            <Label className="text-slate-400 text-[10px] mb-0.5 block">
              {bookingType === "social" ? "Người tổ chức" : "Tên khách hàng"}
            </Label>
            <Input
              value={bookingType === "social" ? organizerName : customerName}
              onChange={(e) => {
                const val = e.target.value;
                if (bookingType === "social") { setOrganizerName(val); setCustomerName(val); }
                else { setCustomerName(val); }
                searchCustomers(val);
              }}
              onFocus={() => customerSuggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="bg-[#0f172a] border-[#334155] text-slate-50 h-8 text-sm"
              placeholder="Nhập tên..."
              data-testid="booking-customer-name"
            />
            {showSuggestions && customerSuggestions.length > 0 && (
              <div className="absolute z-50 w-full bg-[#1e293b] border border-[#334155] rounded-md shadow-lg mt-1 max-h-32 overflow-y-auto">
                {customerSuggestions.map((c) => (
                  <button key={c.id} onMouseDown={(e) => { e.preventDefault(); selectCustomer(c); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-slate-200 hover:bg-[#334155] flex justify-between">
                    <span>{c.name} <span className="text-slate-500">{c.phone}</span></span>
                    {c.booking_count > 0 && <span className="text-[9px] text-lime-400">{c.booking_count}x</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* SĐT + Sân/Số khách */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-slate-400 text-[10px] mb-0.5 block">SDT</Label>
              <Input value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value.replace(/\s/g, ""))}
                onKeyDown={blockWhitespaceKey}
                onBlur={(e) => setCustomerPhone(normalizePhone(e.target.value))}
                className="bg-[#0f172a] border-[#334155] text-slate-50 h-8 text-sm" placeholder="0901..."
                data-testid="booking-customer-phone" />
            </div>
            {bookingType === "regular" ? (
              <div>
                <Label className="text-slate-400 text-[10px] mb-0.5 block">San</Label>
                <Select value={courtId} onValueChange={setCourtId}>
                  <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-50 h-8 text-sm" data-testid="booking-court-select">
                    <SelectValue placeholder="Chọn sân" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e293b] border-[#334155]">
                    {regularCourts.map((c) => (
                      <SelectItem key={c.id} value={c.id} className="text-slate-200 focus:bg-[#334155] text-xs">{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-slate-400 text-[10px] mb-0.5 block">Số vé (người)</Label>
                <Input type="number" min={1} value={guestCount}
                  onChange={(e) => setGuestCount(parseInt(e.target.value) || 0)}
                  className="bg-[#0f172a] border-[#334155] text-slate-50 h-8 text-sm"
                  data-testid="booking-guest-count" />
              </div>
            )}
          </div>

          {/* Ngày + Giờ BĐ + Giờ KT */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-slate-400 text-[10px] mb-0.5 block">Ngay</Label>
              <DateInputVN value={date} onChange={setDate}
                className="bg-[#0f172a] border-[#334155] text-slate-50 h-8 text-sm" data-testid="booking-date" />
            </div>
            <div>
              <Label className="text-slate-400 text-[10px] mb-0.5 block">Bắt đầu</Label>
              <Select value={startTime} onValueChange={setStartTime}>
                <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-50 h-8 text-xs" data-testid="booking-start-time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155] max-h-48">
                  {TIME_OPTIONS.map((t) => <SelectItem key={t} value={t} className="text-slate-200 text-xs">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400 text-[10px] mb-0.5 block">Kết thúc</Label>
              <Select value={endTime} onValueChange={setEndTime}>
                <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-50 h-8 text-xs" data-testid="booking-end-time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155] max-h-48">
                  {TIME_OPTIONS.filter((t) => t > startTime).map((t) => <SelectItem key={t} value={t} className="text-slate-200 text-xs">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Trạng thái + Đặt cọc + Ghi chú */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-slate-400 text-[10px] mb-0.5 block">Trạng thái</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-50 h-8 text-xs" data-testid="booking-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  <SelectItem value="pending" className="text-slate-200 text-xs">Cho XN</SelectItem>
                  <SelectItem value="confirmed" className="text-slate-200 text-xs">Da XN</SelectItem>
                  <SelectItem value="completed" className="text-slate-200 text-xs">Hoan thanh</SelectItem>
                  <SelectItem value="cancelled" className="text-slate-200 text-xs">Huy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400 text-[10px] mb-0.5 block">Đặt cọc</Label>
              <Input type="number" value={deposit} onChange={(e) => setDeposit(e.target.value)}
                className="bg-[#0f172a] border-[#334155] text-slate-50 h-8 text-sm" placeholder="0"
                data-testid="booking-deposit" />
            </div>
            <div>
              <Label className="text-slate-400 text-[10px] mb-0.5 block">Ghi chú</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                className="bg-[#0f172a] border-[#334155] text-slate-50 h-8 text-sm" placeholder="..."
                data-testid="booking-notes" />
            </div>
          </div>

          {/* Checkbox: không lưu khách vãng lai (chỉ khi tạo mới, chưa có customerId) */}
          {!isEdit && !customerId && customerPhone && (
            <label className="flex items-center gap-2 cursor-pointer select-none px-1" data-testid="skip-create-customer-wrapper">
              <input
                type="checkbox"
                checked={skipCreateCustomer}
                onChange={(e) => setSkipCreateCustomer(e.target.checked)}
                className="w-3.5 h-3.5 accent-lime-500"
                data-testid="skip-create-customer-checkbox"
              />
              <span className="text-[11px] text-slate-400">
                Không lưu khách vãng lai <span className="text-slate-500">(bỏ qua tạo tài khoản)</span>
              </span>
            </label>
          )}

          {/* === MULTI-COURT BOOKING (CEO/Staff, regular, tạo mới) === */}
          {!isEdit && bookingType === "regular" && isStaffOrCeo && (
            <div className="rounded-xl bg-[#0f172a] border border-indigo-500/30 p-3 space-y-2" data-testid="multi-court-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-indigo-400" />
                  <span className="text-[11px] font-semibold text-indigo-300">Đặt nhiều sân cùng lúc</span>
                  {multiSlots.length > 0 && (
                    <span className="text-[10px] text-slate-500">({multiSlots.length + 1} sân)</span>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    // Default: cùng giờ với sân chính, court_id của sân kế tiếp
                    const usedIds = new Set([courtId, ...multiSlots.map((s) => s.court_id)]);
                    const nextCourt = regularCourts.find((c) => !usedIds.has(c.id));
                    setMultiSlots((prev) => [
                      ...prev,
                      {
                        court_id: nextCourt?.id || "",
                        start_time: startTime,
                        end_time: endTime,
                      },
                    ]);
                  }}
                  className="h-6 px-2 text-[10px] text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10"
                  data-testid="multi-court-add-btn"
                >
                  <Plus className="w-3 h-3 mr-0.5" /> Thêm sân khác
                </Button>
              </div>

              {multiSlots.length === 0 ? (
                <p className="text-[10px] text-slate-500 italic">
                  Bấm "Thêm sân khác" để đặt thêm sân (có thể khung giờ khác nhau) cho cùng khách.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {multiSlots.map((slot, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-1.5 items-end" data-testid={`multi-slot-${idx}`}>
                      <div className="col-span-5">
                        <Label className="text-slate-500 text-[9px] mb-0.5 block">Sân #{idx + 2}</Label>
                        <Select
                          value={slot.court_id}
                          onValueChange={(v) =>
                            setMultiSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, court_id: v } : s)))
                          }
                        >
                          <SelectTrigger className="bg-[#1e293b] border-[#334155] text-slate-50 h-7 text-xs" data-testid={`multi-slot-court-${idx}`}>
                            <SelectValue placeholder="Chọn sân" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1e293b] border-[#334155] max-h-48">
                            {regularCourts.map((c) => (
                              <SelectItem key={c.id} value={c.id} className="text-slate-200 text-xs">
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Label className="text-slate-500 text-[9px] mb-0.5 block">Bắt đầu</Label>
                        <Select
                          value={slot.start_time}
                          onValueChange={(v) =>
                            setMultiSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, start_time: v } : s)))
                          }
                        >
                          <SelectTrigger className="bg-[#1e293b] border-[#334155] text-slate-50 h-7 text-xs" data-testid={`multi-slot-start-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1e293b] border-[#334155] max-h-48">
                            {TIME_OPTIONS.map((t) => (
                              <SelectItem key={t} value={t} className="text-slate-200 text-xs">
                                {t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Label className="text-slate-500 text-[9px] mb-0.5 block">Kết thúc</Label>
                        <Select
                          value={slot.end_time}
                          onValueChange={(v) =>
                            setMultiSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, end_time: v } : s)))
                          }
                        >
                          <SelectTrigger className="bg-[#1e293b] border-[#334155] text-slate-50 h-7 text-xs" data-testid={`multi-slot-end-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1e293b] border-[#334155] max-h-48">
                            {TIME_OPTIONS.filter((t) => t > slot.start_time).map((t) => (
                              <SelectItem key={t} value={t} className="text-slate-200 text-xs">
                                {t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setMultiSlots((prev) => prev.filter((_, i) => i !== idx))}
                          className="h-7 w-7 flex items-center justify-center text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded"
                          data-testid={`multi-slot-remove-${idx}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== INVOICE SUMMARY ===== */}
          <div className="rounded-xl bg-[#0f172a] border border-[#334155] p-3 mt-1" data-testid="invoice-summary">
            <div className="flex items-center gap-1.5 mb-2">
              <Receipt className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-[10px] text-slate-400 uppercase font-medium">Tổng hợp</span>
            </div>

            {bookingType === "regular" ? (
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">San</span>
                  <span className="text-slate-200">{selectedCourt?.name || "—"}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Thời gian</span>
                  <span className="text-slate-200">{startTime} - {endTime} ({durationHours > 0 ? `${durationHours}h` : "—"})</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Đơn giá</span>
                  <span className="text-slate-300">{formatVND(regularPrice.perHour)}d/h</span>
                </div>
                {deposit > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Đặt cọc</span>
                    <span className="text-amber-400">-{formatVND(deposit)}d</span>
                  </div>
                )}
                <div className="border-t border-[#334155] mt-1.5 pt-1.5 flex justify-between">
                  <span className="text-xs font-semibold text-slate-300">
                    Tổng cộng{multiSlots.length > 0 ? ` (${multiSlots.length + 1} sân)` : ""}
                  </span>
                  <span className="text-sm font-bold text-lime-400" data-testid="invoice-total">
                    {formatVND(grandTotalRegular)}d
                  </span>
                </div>
                {multiSlots.length > 0 && (
                  <div className="text-[10px] text-slate-500 mt-1 space-y-0.5">
                    <div>· {selectedCourt?.name}: {formatVND(regularPrice.total)}đ</div>
                    {multiSlots.map((s, i) => {
                      const c = courts.find((x) => x.id === s.court_id);
                      const p = c ? calcRegularPrice(c, date, s.start_time, s.end_time) : { total: 0 };
                      return (
                        <div key={i}>· {c?.name || "—"} ({s.start_time}-{s.end_time}): {formatVND(p.total)}đ</div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Loại vé</span>
                  <span className="text-slate-200">{timeToMin(startTime) >= 945 ? "Vé chiều" : "Vé sáng"}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Giá vé</span>
                  <span className="text-slate-300">{formatVND(socialPrice.pricePerPerson)}d/nguoi</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Số vé</span>
                  <span className="text-slate-200">{guestCount} ve</span>
                </div>
                {deposit > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Đặt cọc</span>
                    <span className="text-amber-400">-{formatVND(deposit)}d</span>
                  </div>
                )}
                <div className="border-t border-[#334155] mt-1.5 pt-1.5 flex justify-between">
                  <span className="text-xs font-semibold text-slate-300">Tổng cộng</span>
                  <span className="text-sm font-bold text-teal-400">{formatVND(socialPrice.total)}d</span>
                </div>
                <div className="text-[10px] text-slate-500 text-right">
                  {guestCount} người x {formatVND(socialPrice.pricePerPerson)}d = {formatVND(socialPrice.total)}d
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="border-[#334155] text-slate-300 hover:bg-[#334155] h-8 text-xs">
            Huy
          </Button>
          {isEdit && (
            <Button variant="outline" onClick={handleDelete}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 text-xs"
              data-testid="delete-booking-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Xoa
            </Button>
          )}
          <Button onClick={handleSubmit}
            disabled={submitting || !customerName || !startTime || !endTime}
            className={`font-semibold h-8 text-xs ${bookingType === "social" ? "bg-teal-500 text-[#0f172a] hover:bg-teal-400" : "bg-lime-500 text-[#0f172a] hover:bg-lime-400"}`}
            data-testid="submit-booking-btn">
            {submitting ? "..." : isEdit ? "Cập nhật" : "Đặt sân"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
